import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield, ShieldAlert, Plus, Pencil, Trash2, Play, FlaskConical, RefreshCw, Sparkles,
  ArrowLeft, Ban, X, CheckCircle2, AlertTriangle, ListChecks,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { flagEmoji, fmtTime } from "@/lib/regie";
import { ALL_COUNTRIES } from "@/lib/countries";

/**
 * Page /admin/securite/blocage-automatique (root uniquement)
 *
 * Moteur de règles : chaque règle combine des conditions (pays, identification
 * robot, cadence de requêtes, chemins suspects, échecs de connexion…) et une
 * action (bloquer l'IP, l'appareil, les deux, ou journaliser), temporaire ou non.
 * Mode simulation, liste blanche, règles types, journal des décisions, test d'IP.
 */

const BOT_LABELS = { human: "Humain", verified: "Robot vérifié (Google/Bing)", impostor: "Faux robot (imposteur)", bot: "Autre robot / outil" };
const ACTION_STYLES = {
  block_ip: "bg-red-100 text-red-700 border-red-200",
  block_device: "bg-red-100 text-red-700 border-red-200",
  block_both: "bg-red-100 text-red-700 border-red-200",
  log_only: "bg-amber-50 text-amber-800 border-amber-200",
};

const Lbl = ({ children }) => <label className="text-[11px] uppercase tracking-[0.14em] text-ash">{children}</label>;
const inputCls = "w-full mt-1 border border-stone rounded-sm px-3 py-2 text-sm bg-white";

const emptyRule = () => ({
  name: "", description: "", enabled: true, priority: 100, match: "all",
  conditions: [{ field: "requests_1m", op: "gte", value: 120 }],
  action: { type: "block_ip", duration_hours: 24, reason: "" },
});

function valueToText(v) {
  if (Array.isArray(v)) return v.join(", ");
  if (v === null || v === undefined) return "";
  return String(v);
}

function ConditionRow({ cond, catalog, onChange, onRemove }) {
  const spec = catalog.fields[cond.field] || {};
  const ops = Object.entries(catalog.operators).filter(([, o]) => o.types.includes(spec.type));
  const needsValue = !["is_empty", "is_not_empty"].includes(cond.op);
  const isList = ["in", "not_in"].includes(cond.op);

  const renderValue = () => {
    if (!needsValue) return null;
    if (spec.type === "bool") {
      return (
        <select className={inputCls} value={String(cond.value)} onChange={(e) => onChange({ ...cond, value: e.target.value === "true" })}>
          <option value="true">vrai</option>
          <option value="false">faux</option>
        </select>
      );
    }
    if (spec.type === "enum" && !isList) {
      return (
        <select className={inputCls} value={cond.value ?? ""} onChange={(e) => onChange({ ...cond, value: e.target.value })}>
          <option value="">—</option>
          {spec.values.map((v) => <option key={v} value={v}>{cond.field === "bot_type" ? BOT_LABELS[v] || v : v}</option>)}
        </select>
      );
    }
    if (spec.type === "enum_country" && !isList) {
      return (
        <select className={inputCls} value={cond.value ?? ""} onChange={(e) => onChange({ ...cond, value: e.target.value })}>
          <option value="">—</option>
          {ALL_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{flagEmoji(c.code)} {c.name} ({c.code})</option>)}
        </select>
      );
    }
    if (spec.type === "number") {
      return <input type="number" className={inputCls} value={cond.value ?? ""} onChange={(e) => onChange({ ...cond, value: e.target.value })} />;
    }
    return (
      <input
        className={inputCls}
        value={valueToText(cond.value)}
        placeholder={isList ? "valeurs séparées par des virgules (ex : US, RU, CN)" : spec.type === "enum_country" ? "ex : US" : ""}
        onChange={(e) => onChange({ ...cond, value: e.target.value })}
      />
    );
  };

  return (
    <div className="grid md:grid-cols-[1.3fr_1fr_1.3fr_auto] gap-2 items-end bg-sand/40 border border-stone rounded-sm p-3">
      <div>
        <Lbl>Champ</Lbl>
        <select
          className={inputCls}
          value={cond.field}
          onChange={(e) => {
            const f = e.target.value;
            const t = catalog.fields[f]?.type;
            const firstOp = Object.entries(catalog.operators).find(([, o]) => o.types.includes(t))?.[0] || "eq";
            onChange({ field: f, op: firstOp, value: t === "bool" ? true : t === "number" ? 1 : "" });
          }}
        >
          {Object.entries(catalog.fields).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
        </select>
        {spec.help && <p className="text-[10px] text-ash mt-1 leading-snug">{spec.help}</p>}
      </div>
      <div>
        <Lbl>Opérateur</Lbl>
        <select className={inputCls} value={cond.op} onChange={(e) => onChange({ ...cond, op: e.target.value, value: ["is_empty", "is_not_empty"].includes(e.target.value) ? null : cond.value })}>
          {ops.map(([k, o]) => <option key={k} value={k}>{o.label}</option>)}
        </select>
      </div>
      <div>{needsValue && <Lbl>Valeur</Lbl>}{renderValue()}</div>
      <button type="button" onClick={onRemove} className="p-2 text-ash hover:text-red-600 mb-0.5" title="Retirer la condition"><X className="h-4 w-4" /></button>
    </div>
  );
}

function RuleEditor({ initial, catalog, onClose, onSaved }) {
  const [form, setForm] = useState(initial || emptyRule());
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial?.id);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setAction = (patch) => setForm((f) => ({ ...f, action: { ...f.action, ...patch } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, priority: Number(form.priority) || 100 };
      if (editing) await api.put(`/root/autoblock/rules/${initial.id}`, payload);
      else await api.post("/root/autoblock/rules", payload);
      toast.success(editing ? "Règle mise à jour" : "Règle créée");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-ink/60 flex items-start justify-center overflow-y-auto p-4">
      <form onSubmit={submit} className="bg-cream w-full max-w-3xl border border-stone rounded-sm p-6 space-y-5 my-6" data-testid="rule-editor">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-ink">{editing ? "Modifier la règle" : "Nouvelle règle"}</h2>
          <button type="button" onClick={onClose} className="text-ash hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid md:grid-cols-[2fr_1fr] gap-3">
          <div><Lbl>Nom</Lbl><input className={inputCls} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Ex : Robot US en rafale" required /></div>
          <div><Lbl>Priorité (plus petit = évalué en premier)</Lbl><input type="number" className={inputCls} value={form.priority} onChange={(e) => set({ priority: e.target.value })} /></div>
        </div>
        <div><Lbl>Description (optionnel)</Lbl><input className={inputCls} value={form.description || ""} onChange={(e) => set({ description: e.target.value })} /></div>

        <div>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <Lbl>Conditions</Lbl>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-graphite">Déclencher si</span>
              <select className="border border-stone rounded-sm px-2 py-1 bg-white text-xs" value={form.match} onChange={(e) => set({ match: e.target.value })}>
                <option value="all">TOUTES les conditions sont vraies</option>
                <option value="any">AU MOINS UNE condition est vraie</option>
              </select>
            </div>
          </div>
          <div className="space-y-2">
            {form.conditions.map((c, i) => (
              <ConditionRow
                key={i}
                cond={c}
                catalog={catalog}
                onChange={(nc) => set({ conditions: form.conditions.map((x, j) => (j === i ? nc : x)) })}
                onRemove={() => set({ conditions: form.conditions.filter((_, j) => j !== i) })}
              />
            ))}
          </div>
          <button type="button" onClick={() => set({ conditions: [...form.conditions, { field: "bot_type", op: "eq", value: "bot" }] })} className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.1em] text-graphite border border-stone bg-white px-3 py-1.5 hover:bg-sand rounded-sm">
            <Plus className="h-3.5 w-3.5" /> Ajouter une condition
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-3 bg-white border border-stone rounded-sm p-3">
          <div>
            <Lbl>Action</Lbl>
            <select className={inputCls} value={form.action.type} onChange={(e) => setAction({ type: e.target.value })}>
              {Object.entries(catalog.actions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <Lbl>Durée (heures, vide = définitif)</Lbl>
            <input type="number" min="0" className={inputCls} value={form.action.duration_hours ?? ""} onChange={(e) => setAction({ duration_hours: e.target.value === "" ? null : Number(e.target.value) })} disabled={form.action.type === "log_only"} />
          </div>
          <div>
            <Lbl>Motif affiché dans les listes</Lbl>
            <input className={inputCls} value={form.action.reason || ""} onChange={(e) => setAction({ reason: e.target.value })} placeholder={form.name || "Motif"} />
          </div>
        </div>

        <div className="flex items-center justify-between border border-stone rounded-sm p-3 bg-white">
          <div><Lbl>Règle active</Lbl><p className="text-xs text-graphite mt-0.5">Une règle désactivée est conservée mais jamais évaluée.</p></div>
          <Switch checked={form.enabled} onCheckedChange={(v) => set({ enabled: v })} />
        </div>

        <button type="submit" disabled={saving} className="w-full bg-ink text-cream py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
          {saving ? "Enregistrement…" : editing ? "Mettre à jour la règle" : "Créer la règle"}
        </button>
      </form>
    </div>
  );
}

function describe(rule, catalog) {
  const parts = (rule.conditions || []).map((c) => {
    const f = catalog.fields[c.field]?.label || c.field;
    const o = catalog.operators[c.op]?.label || c.op;
    let v = valueToText(c.value);
    if (c.field === "bot_type") v = Array.isArray(c.value) ? c.value.map((x) => BOT_LABELS[x] || x).join(", ") : BOT_LABELS[c.value] || v;
    return `${f} ${o} ${v}`.trim();
  });
  return parts.join(rule.match === "any" ? "  OU  " : "  ET  ");
}

export default function AdminAutoBlock() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | rule
  const [savingCfg, setSavingCfg] = useState(false);
  const [allowText, setAllowText] = useState("");
  const [testIp, setTestIp] = useState("");
  const [testUa, setTestUa] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [loadError, setLoadError] = useState(null); // { status, detail, build }

  const load = useCallback(() => {
    setLoadError(null);
    Promise.all([api.get("/root/autoblock"), api.get("/root/autoblock/events", { params: { limit: 80 } })])
      .then(([a, e]) => { setData(a.data); setAllowText((a.data.allowlist || []).join("\n")); setEvents(e.data.events || []); })
      .catch(async (err) => {
        const status = err.response?.status;
        let build = null;
        try { const { data: h } = await api.get("/health"); build = h?.build || null; } catch { /* ignore */ }
        setLoadError({ status, detail: formatApiError(err.response?.data?.detail), build });
      });
  }, []);
  useEffect(() => { if (user?.is_root) load(); }, [user, load]);

  const catalog = data?.catalog;
  const rules = useMemo(() => [...(data?.rules || [])].sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100)), [data]);

  const saveCfg = async (patch) => {
    setSavingCfg(true);
    try {
      const { data: cfg } = await api.put("/root/autoblock/config", patch);
      setData((d) => ({ ...d, ...cfg }));
      toast.success("Configuration enregistrée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingCfg(false);
    }
  };

  const toggleRule = async (rule, enabled) => {
    setBusyId(rule.id);
    try {
      await api.put(`/root/autoblock/rules/${rule.id}`, { enabled });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };
  const deleteRule = async (rule) => {
    if (!window.confirm(`Supprimer la règle « ${rule.name} » ?`)) return;
    setBusyId(rule.id);
    try {
      await api.delete(`/root/autoblock/rules/${rule.id}`);
      toast.success("Règle supprimée");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };
  const addPreset = async (preset) => {
    setBusyId(preset.key);
    try {
      await api.post("/root/autoblock/rules", { preset: preset.key });
      toast.success(`Règle « ${preset.name} » ajoutée`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };
  const runTest = async () => {
    if (!testIp.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data: r } = await api.post("/root/autoblock/test", { ip: testIp.trim(), user_agent: testUa || undefined });
      setTestResult(r);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  if (user?.role !== "superadmin" || !user?.is_root) return <p className="text-graphite">Accès réservé au compte racine.</p>;

  return (
    <div data-testid="admin-autoblock">
      <Link to="/admin/securite" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-ash hover:text-ink mb-4"><ArrowLeft className="h-3.5 w-3.5" /> Sécurité</Link>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Sécurité · Root</p>
          <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">Blocage automatique</h1>
          <p className="text-sm text-graphite mt-2 max-w-2xl">
            Des règles évaluées sur chaque requête publique : conditions sur le pays, l'identification robot, la cadence d'appels,
            les chemins suspects, les échecs de connexion… et une action, temporaire ou définitive. La première règle qui correspond décide.
            Les robots vérifiés (Google, Bing), les IP privées, la liste blanche et les IP des administrateurs ne sont jamais bloqués automatiquement.
          </p>
        </div>
        <button onClick={() => setEditing({})} data-testid="new-rule" className="flex items-center gap-2 bg-ink text-cream px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors">
          <Plus className="h-4 w-4" /> Nouvelle règle
        </button>
      </div>

      {loadError ? (
        <div className="bg-red-50 border border-red-200 rounded-sm p-5 text-sm" data-testid="autoblock-load-error">
          <p className="text-red-700 font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Impossible de charger le module ({loadError.status || "réseau"} — {loadError.detail}).</p>
          {loadError.status === 404 ? (
            <p className="text-graphite mt-2">
              L'API répond mais ne connaît pas encore les routes du blocage automatique : <strong>le backend déployé est antérieur à ce module</strong>.
              Version backend en ligne : <span className="font-mono">{loadError.build || "inconnue (pas de marqueur de build)"}</span>, version attendue : <span className="font-mono">2026-09-03.1-regles-auto</span> ou plus récente.
              Relancez le déploiement du backend sur Coolify, puis rechargez cette page.
            </p>
          ) : loadError.status === 403 ? (
            <p className="text-graphite mt-2">Ce module est réservé au compte racine.</p>
          ) : (
            <p className="text-graphite mt-2">Le backend n'a pas répondu (réseau, CORS ou service arrêté). Vérifiez l'état du backend sur Coolify.</p>
          )}
          <button onClick={load} className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.1em] border border-stone bg-white px-3 py-1.5 text-graphite hover:bg-sand rounded-sm"><RefreshCw className="h-3.5 w-3.5" /> Réessayer</button>
        </div>
      ) : !data ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : (
        <div className="space-y-6">
          {/* État global */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className={`border rounded-sm p-5 ${data.enabled ? "bg-white border-stone" : "bg-amber-50 border-amber-200"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-ash">Moteur de règles</p>
                  <p className="text-sm text-ink font-medium mt-1">{data.enabled ? "Activé" : "Désactivé"}</p>
                </div>
                <Switch data-testid="autoblock-enabled" checked={Boolean(data.enabled)} disabled={savingCfg} onCheckedChange={(v) => saveCfg({ enabled: v })} />
              </div>
              <p className="text-xs text-graphite mt-2">{data.enabled ? "Les règles actives sont évaluées sur chaque requête." : "Aucune règle n'est évaluée tant que le moteur est désactivé."}</p>
            </div>
            <div className={`border rounded-sm p-5 ${data.dry_run ? "bg-amber-50 border-amber-200" : "bg-white border-stone"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-ash">Mode simulation</p>
                  <p className="text-sm text-ink font-medium mt-1">{data.dry_run ? "Simulation : rien n'est bloqué" : "Réel : les actions s'appliquent"}</p>
                </div>
                <Switch data-testid="autoblock-dryrun" checked={Boolean(data.dry_run)} disabled={savingCfg} onCheckedChange={(v) => saveCfg({ dry_run: v })} />
              </div>
              <p className="text-xs text-graphite mt-2">Conseillé au départ : les décisions sont journalisées ci-dessous sans bloquer, pour vérifier les règles avant de passer en réel.</p>
            </div>
            <div className="bg-white border border-stone rounded-sm p-5">
              <p className="text-[11px] uppercase tracking-[0.14em] text-ash">Dernières 24 h</p>
              <div className="flex items-baseline gap-4 mt-1">
                <div><p className="font-serif text-3xl text-ink">{data.stats?.events_24h ?? 0}</p><p className="text-[11px] text-ash">décisions</p></div>
                <div><p className="font-serif text-3xl text-red-600">{data.stats?.blocks_24h ?? 0}</p><p className="text-[11px] text-ash">blocages appliqués</p></div>
                <div><p className="font-serif text-3xl text-ink">{rules.filter((r) => r.enabled).length}<span className="text-base text-ash">/{rules.length}</span></p><p className="text-[11px] text-ash">règles actives</p></div>
              </div>
            </div>
          </div>

          {/* Règles */}
          <div className="bg-white border border-stone rounded-sm p-5" data-testid="rules-list">
            <div className="flex items-center gap-2 mb-4"><ListChecks className="h-5 w-5 text-copper" /><h2 className="font-serif text-xl text-ink">Règles</h2><span className="text-xs text-ash">évaluées par priorité croissante, la première qui correspond s'applique</span></div>
            {rules.length === 0 ? (
              <p className="text-sm text-ash italic">Aucune règle. Ajoutez une règle type ci-dessous ou créez la vôtre.</p>
            ) : (
              <div className="divide-y divide-stone border border-stone rounded-sm overflow-hidden">
                {rules.map((r) => (
                  <div key={r.id} className={`p-4 ${r.enabled ? "bg-white" : "bg-sand/40 opacity-70"}`} data-testid={`rule-${r.id}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-[10px] text-ash w-8">#{r.priority ?? 100}</span>
                      <p className="font-medium text-ink">{r.name}</p>
                      <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border ${ACTION_STYLES[r.action?.type] || ""}`}>
                        {catalog.actions[r.action?.type] || r.action?.type}{r.action?.type !== "log_only" ? (r.action?.duration_hours ? ` · ${r.action.duration_hours} h` : " · définitif") : ""}
                      </span>
                      {r.hits > 0 && <span className="text-[11px] text-red-600">{r.hits} déclenchement{r.hits > 1 ? "s" : ""}{r.last_triggered_at ? ` · dernier ${fmtTime(r.last_triggered_at)}` : ""}</span>}
                      <span className="flex-1" />
                      <Switch checked={Boolean(r.enabled)} disabled={busyId === r.id} onCheckedChange={(v) => toggleRule(r, v)} />
                      <button onClick={() => setEditing(r)} className="p-2 text-graphite hover:text-ink" title="Modifier"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => deleteRule(r)} disabled={busyId === r.id} className="p-2 text-graphite hover:text-red-600" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <p className="text-xs text-graphite mt-1.5 pl-11">{describe(r, catalog)}</p>
                    {r.description && <p className="text-[11px] text-ash mt-0.5 pl-11">{r.description}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Règles types */}
          <div className="bg-white border border-stone rounded-sm p-5">
            <div className="flex items-center gap-2 mb-1"><Sparkles className="h-5 w-5 text-copper" /><h2 className="font-serif text-xl text-ink">Règles types</h2></div>
            <p className="text-xs text-graphite mb-4">Ajoutez-les en un clic puis ajustez les seuils, durées ou pays selon votre trafic.</p>
            <div className="grid md:grid-cols-2 gap-3">
              {catalog.presets.map((p) => {
                const already = rules.some((r) => r.name === p.name);
                return (
                  <div key={p.key} className="border border-stone rounded-sm p-3 bg-sand/30">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-ink">{p.name}</p>
                        <p className="text-[11px] text-graphite mt-0.5">{p.description}</p>
                        <p className="text-[11px] text-ash mt-1">{describe(p, catalog)} → {catalog.actions[p.action.type]}{p.action.duration_hours ? ` ${p.action.duration_hours} h` : ""}</p>
                      </div>
                      <button onClick={() => addPreset(p)} disabled={busyId === p.key} className={`shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] px-2.5 py-1.5 rounded-sm border ${already ? "text-ash border-stone bg-white" : "bg-ink text-cream border-ink hover:bg-gold hover:text-ink"} disabled:opacity-50`}>
                        {already ? <><CheckCircle2 className="h-3 w-3" /> Ajouter encore</> : <><Plus className="h-3 w-3" /> Ajouter</>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Liste blanche */}
            <div className="bg-white border border-stone rounded-sm p-5">
              <div className="flex items-center gap-2 mb-1"><Shield className="h-5 w-5 text-copper" /><h2 className="font-serif text-xl text-ink">Liste blanche</h2></div>
              <p className="text-xs text-graphite mb-3">IP ou plages CIDR jamais bloquées automatiquement (une par ligne). Ajoutez-y vos bureaux, vos outils de monitoring, vos partenaires. Les IP des administrateurs connectés dans les 7 derniers jours sont protégées d'office.</p>
              <textarea rows={5} className="w-full border border-stone rounded-sm px-3 py-2 text-sm font-mono bg-white" value={allowText} onChange={(e) => setAllowText(e.target.value)} placeholder={"41.140.0.0/16\n89.92.160.195"} />
              <button onClick={() => saveCfg({ allowlist: allowText.split("\n").map((s) => s.trim()).filter(Boolean) })} disabled={savingCfg} className="mt-3 bg-ink text-cream px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                {savingCfg ? "Enregistrement…" : "Enregistrer la liste blanche"}
              </button>
            </div>

            {/* Tester une IP */}
            <div className="bg-white border border-stone rounded-sm p-5" data-testid="autoblock-test">
              <div className="flex items-center gap-2 mb-1"><FlaskConical className="h-5 w-5 text-copper" /><h2 className="font-serif text-xl text-ink">Tester une IP</h2></div>
              <p className="text-xs text-graphite mb-3">Calcule les faits connus pour cette IP (pays, robot, cadence actuelle, sessions…) et indique quelle règle s'appliquerait, sans rien bloquer.</p>
              <div className="flex gap-2 flex-wrap">
                <input className="border border-stone rounded-sm px-3 py-2 text-sm bg-white font-mono flex-1 min-w-[160px]" value={testIp} onChange={(e) => setTestIp(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runTest()} placeholder="160.177.76.250" />
                <input className="border border-stone rounded-sm px-3 py-2 text-sm bg-white flex-[2] min-w-[200px]" value={testUa} onChange={(e) => setTestUa(e.target.value)} placeholder="User-Agent (optionnel, sinon dernier connu)" />
                <button onClick={runTest} disabled={testing || !testIp.trim()} className="inline-flex items-center gap-1.5 bg-ink text-cream px-4 py-2 text-xs uppercase tracking-[0.12em] hover:bg-gold hover:text-ink rounded-sm disabled:opacity-50"><Play className="h-3.5 w-3.5" /> Tester</button>
              </div>
              {testResult && (
                <div className="mt-4 space-y-3 text-sm">
                  <div className={`border rounded-sm p-3 ${testResult.matched_rule ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
                    {testResult.matched_rule ? (
                      <p className="text-red-700 flex items-center gap-2"><Ban className="h-4 w-4" /> Règle appliquée : <strong>{testResult.matched_rule.name}</strong> → {catalog.actions[testResult.matched_rule.action?.type]}{testResult.dry_run ? " (simulation active : non appliqué)" : ""}</p>
                    ) : (
                      <p className="text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Aucune règle ne correspond actuellement.</p>
                    )}
                    {testResult.allowlisted && <p className="text-xs text-graphite mt-1">Cette IP est en liste blanche : jamais bloquée automatiquement.</p>}
                    {testResult.admin_ip && <p className="text-xs text-graphite mt-1">IP d'un administrateur : protégée, jamais bloquée automatiquement.</p>}
                    {!testResult.enabled && <p className="text-xs text-amber-800 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Le moteur est désactivé.</p>}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                    {Object.entries(catalog.fields).map(([k, f]) => (
                      <div key={k} className="bg-sand/40 border border-stone rounded-sm px-2 py-1.5">
                        <p className="text-[10px] uppercase tracking-[0.1em] text-ash truncate">{f.label}</p>
                        <p className="text-ink font-mono truncate" title={valueToText(testResult.facts[k])}>
                          {k === "bot_type" ? BOT_LABELS[testResult.facts[k]] || testResult.facts[k] : k === "country" && testResult.facts[k] ? `${flagEmoji(testResult.facts[k])} ${testResult.facts[k]}` : valueToText(testResult.facts[k]) || "—"}
                        </p>
                      </div>
                    ))}
                  </div>
                  {testResult.rules?.length > 0 && (
                    <div className="space-y-1">
                      {testResult.rules.map((r) => (
                        <div key={r.id} className="text-xs flex items-center gap-2">
                          {r.matches ? <Ban className="h-3.5 w-3.5 text-red-600" /> : <X className="h-3.5 w-3.5 text-ash" />}
                          <span className={r.matches ? "text-red-700 font-medium" : "text-graphite"}>{r.name}</span>
                          <span className="text-ash">{r.conditions.filter((c) => c.ok).length}/{r.conditions.length} conditions vraies</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Journal des décisions */}
          <div className="bg-white border border-stone rounded-sm p-5" data-testid="autoblock-events">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-copper" /><h2 className="font-serif text-xl text-ink">Journal des décisions</h2></div>
              <button onClick={load} className="p-2 border border-stone bg-white text-graphite hover:bg-sand rounded-sm" title="Rafraîchir"><RefreshCw className="h-4 w-4" /></button>
            </div>
            {events.length === 0 ? (
              <p className="text-sm text-ash italic">Aucune décision enregistrée pour le moment.</p>
            ) : (
              <div className="divide-y divide-stone border border-stone rounded-sm overflow-hidden">
                {events.map((ev) => (
                  <div key={ev.id} className={`px-4 py-2.5 text-xs flex items-center gap-3 flex-wrap ${ev.effective_action === "log_only" ? "bg-amber-50/40" : "bg-red-50/40"}`}>
                    <span className="font-mono text-ash whitespace-nowrap">{fmtTime(ev.at)}</span>
                    <span className="font-mono text-ink">{ev.ip}</span>
                    {ev.country && <span>{flagEmoji(ev.country)} {ev.country}</span>}
                    {ev.bot_type && ev.bot_type !== "human" && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-sm border bg-stone/40 text-graphite border-stone">{BOT_LABELS[ev.bot_type] || ev.bot_type}{ev.bot_name ? ` · ${ev.bot_name}` : ""}</span>}
                    <span className="text-ink font-medium">{ev.rule_name}</span>
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${ACTION_STYLES[ev.effective_action] || ""}`}>
                      {ev.simulated ? "simulé · " : ev.protected_admin_ip ? "IP admin protégée · " : ""}{catalog.actions[ev.effective_action] || ev.effective_action}{ev.duration_hours ? ` ${ev.duration_hours} h` : ""}
                    </span>
                    <span className="text-ash truncate max-w-[220px]" title={ev.path}>{ev.path}</span>
                    {ev.facts?.requests_1m != null && <span className="text-ash">{ev.facts.requests_1m} req/min</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {editing && catalog && (
        <RuleEditor
          initial={editing.id ? editing : null}
          catalog={catalog}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
